import React, { useEffect, useState } from 'react';
import { Empty, message } from 'antd';
import { useProductIQApi } from 'api/product_iq';
import { Product, SupplierProductsData } from 'models/SupplierDynamics';
import { ProductsList } from './ProductsList';
import { CompareFooter } from '../compare-footer/compare-footer';
import CompareProductsModal from './CompareProductsModal';

interface ProductIQProductsTabProps {
  vendorDomain?: string;
}

/**
 * ProductIQProductsTab renders Product IQ products for a supplier
 * inside Supplier Dynamics and handles product comparison actions.
 * @param {ProductIQProductsTabProps} props - Tab props.
 * @param {string} [props.vendorDomain] - Selected supplier domain.
 * @returns {React.ReactElement} Product IQ products tab content.
 */
export const ProductIQProductsTab: React.FC<ProductIQProductsTabProps> = ({ vendorDomain }): React.ReactElement => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState<boolean>(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(false);
  const [hasMoreProducts, setHasMoreProducts] = useState<boolean>(true);

  const { fetchProductIQProducts } = useProductIQApi();

  useEffect(() => {
    if (!vendorDomain) {
      setProducts([]);
      setSelectedProducts([]);
      setIsCompareModalOpen(false);
      setHasMoreProducts(false);
      setIsLoadingProducts(false);
      return;
    }

    setIsLoadingProducts(true);
    setProducts([]);
    setSelectedProducts([]);
    setIsCompareModalOpen(false);

    (async (): Promise<void> => {
      try {
        const response: SupplierProductsData | undefined = await fetchProductIQProducts(vendorDomain, 1, 50);
        const productsData: Product[] =
          response?.content?.map((item) => ({
            name: item.name,
            overview: item.overview,
            category: item.category,
            features: item.features || [],
            vendor_domain: vendorDomain
          })) || [];

        setProducts(productsData);
        setHasMoreProducts(Boolean(response?.pagination?.next));
      } catch (error) {
        console.error('Error fetching Product IQ products:', error);
        message.error('Failed to fetch Product IQ products');
        setProducts([]);
        setHasMoreProducts(false);
      } finally {
        setIsLoadingProducts(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorDomain]);

  /**
   * Adds a product to comparison selection.
   * @param {Product} product - Product to add for comparison.
   * @returns {void}
   */
  const addToSelectedProducts = (product: Product): void => {
    if (selectedProducts.find((item) => item.name === product.name)) {
      message.error('Product already selected');
      return;
    }
    if (selectedProducts.length >= 3) {
      message.error('You can only select up to 3 products');
      return;
    }

    setSelectedProducts((prevProducts) => [...prevProducts, { ...product, vendor_domain: vendorDomain || '' }]);
  };

  /**
   * Removes a product from the comparison selection.
   * @param {Product} product - Product to remove.
   * @returns {void}
   */
  const handleRemoveProduct = (product: Product): void => {
    setSelectedProducts((prevProducts) => prevProducts.filter((item) => item.name !== product.name));
  };

  /**
   * Closes the comparison modal and resets selection.
   * @returns {void}
   */
  const handleModalClose = (): void => {
    setIsCompareModalOpen(false);
    setSelectedProducts([]);
  };

  if (!vendorDomain) {
    return (
      <div className="p-4">
        <Empty description="No Product IQ data available for this supplier" />
      </div>
    );
  }

  return (
    <>
      <ProductsList
        vendorDomain={vendorDomain!}
        addToSelectedProducts={addToSelectedProducts}
        productList={products}
        loadingProducts={isLoadingProducts}
        hasMoreProducts={hasMoreProducts}
        setHasMoreProducts={setHasMoreProducts}
      />
      <CompareFooter
        products={selectedProducts}
        onCompare={() => setIsCompareModalOpen(true)}
        onRemoveProduct={handleRemoveProduct}
      />
      <CompareProductsModal
        products={selectedProducts}
        vendorDomain={vendorDomain!}
        title="Competitors Comparison Table"
        open={isCompareModalOpen}
        onCancel={handleModalClose}
        cancelText="Close"
      />
    </>
  );
};
